import { useState } from "react";
import { FunnelSimple, MagnifyingGlass, X } from "@phosphor-icons/react";
import { t } from "i18next";
import { useForm } from "react-hook-form";

import { useDebounce, useSearch } from "@/hooks";
import { ContentLayout } from "@/layout";
import { Heading, SectionHeader } from "@/ui";
import { tw } from "@/utils";
import { MeditationsTable } from "./MeditationsTable";

const inputClass =
  "block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600";

const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-neutral-500";

export const Meditations = () => {
  const { searchQuery, handleSearchChange, setSearchQuery } = useSearch("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { watch, register, reset } = useForm<{
    radioOption: string[];
    titleQuery: string;
    teacherQuery: string;
  }>({
    defaultValues: {
      radioOption: ["paid", "pending payment"],
      titleQuery: "",
      teacherQuery: "",
    },
  });
  const selectedRadioValue = watch("radioOption");
  const titleQuery = watch("titleQuery");
  const teacherQuery = watch("teacherQuery");

  const debouncedTitle = useDebounce(titleQuery);
  const debouncedTeacher = useDebounce(teacherQuery);

  const activeFilterCount = [!!titleQuery, !!teacherQuery].filter(
    Boolean,
  ).length;
  const hasActiveFilters = !!searchQuery || activeFilterCount > 0;

  const clearAll = () => {
    setSearchQuery("");
    reset({
      radioOption: ["paid", "pending payment"],
      titleQuery: "",
      teacherQuery: "",
    });
  };

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <SectionHeader
        left={<Heading level={3} heading={t("general.meditations")} />}
      />

      {/* Search + Filters toggle */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex w-full flex-row items-center sm:max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlass
                size={20}
                className={tw(
                  "text-neutral-950",
                  !searchQuery && "text-neutral-500",
                )}
              />
            </div>
            <input
              type="text"
              id="search"
              placeholder={t("meditations.search_placeholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              className={tw(inputClass, "pl-10")}
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className={tw(
              "flex items-center gap-2 rounded-xl border border-neutral-400 bg-white p-3 text-sm font-medium text-neutral-950 hover:bg-neutral-50",
              filtersOpen && "border-primary-600 bg-primary-50",
            )}
            aria-expanded={filtersOpen}
          >
            <FunnelSimple size={18} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-700 px-1.5 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-900 sm:ml-auto"
            >
              <X size={16} />
              Clear all
            </button>
          )}
        </div>

        {/* Filters panel */}
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Title</span>
              <input
                type="text"
                placeholder="Filter by title"
                className={inputClass}
                {...register("titleQuery")}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Teacher</span>
              <input
                type="text"
                placeholder="Filter by teacher"
                className={inputClass}
                {...register("teacherQuery")}
              />
            </div>
          </div>
        )}
      </div>

      <MeditationsTable
        selectedBundles={[]}
        searchQuery={searchQuery}
        approvedTab
        selectedRadioValue={selectedRadioValue}
        filters={{
          title: debouncedTitle,
          teacher: debouncedTeacher,
        }}
      />
    </ContentLayout>
  );
};
